import React, { useContext, useEffect } from 'react';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { useTranslation } from 'react-i18next';
import { when } from 'mobx';
import { Helmet } from 'react-helmet';

import { observer, useLocalStore } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { storesContext } from 'stores';
import CatalogIcon from '@patternfly/react-icons/dist/esm/icons/catalog-icon';

import {
  CatalogLayout,
  CenteredLoadingSpinner,
  useFeature,
} from '@ole-ui/ole-ui-components';

import {
  Title,
  EmptyState,
  EmptyStateIcon,
  EmptyStateBody,
  Pagination,
  PaginationVariant,
} from '@patternfly/react-core';

import { EnablementFeatureFlags } from 'config/featureFlags';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import Filters from 'components/PremiumFilter';
import LimitedTrialLimitReachAlert from 'components/Subscriptions/LimitedTrial/LimitReachedAlert';
import LegacyLiveSessionList from './LegacyLiveSessionList';

import './styles.scss';

import LiveSessionsStore from './store';

const LiveSessionList = React.lazy(
  () => import(/* webpackChunkName: 'catalog' */ './LiveSessionList'),
);

const pageOptions = [
  {
    title: '10',
    value: 10,
  },
  {
    title: '20',
    value: 20,
  },
  {
    title: '30',
    value: 30,
  },
];

const LiveSessions = () => {
  const { i18n, t } = useTranslation();
  const {
    catalogStore,
    routerStore,
    userStore,
    vocabularyStore,
    premiumSessionsStore,
    premiumSessionFilterStore,
    classesStore,
  } = useContext(storesContext);

  const newCardsEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewPremiumCatalog,
    defaultFlagValue: false,
  });

  const route = routerStore?.route as any;
  const pageTitle = route?.params?.title || t('Live Sessions');

  const liveSessionsStore = useLocalStore(
    () => new LiveSessionsStore(catalogStore, premiumSessionFilterStore),
  );

  const {
    paginatedEntries,
    currentPage,
    totalPages,
    totalCount,
    setCurrentPage,
    entriesPerPage,
  } = liveSessionsStore;

  const presetFilters = () => {
    premiumSessionFilterStore.presetFilters();
  };

  useEffect(() => {
    // Populate entry data and filters on initial load
    when(
      () => !catalogStore.loaded && userStore.subscription.subscription,
      async () => {
        catalogStore.getFilterInfo();
      },
    );
  }, []);

  // Premium sessions
  useEffect(() => {
    // // Select preset filters on initial load
    when(
      () => catalogStore.loaded && premiumSessionsStore.loaded && route?.name,
      () => {
        if (
          catalogStore.loaded &&
          premiumSessionsStore.loaded &&
          !liveSessionsStore.ready
        ) {
          premiumSessionFilterStore.getFilterInfo(
            i18n,
            t,
            vocabularyStore,
            liveSessionsStore.filterForScheduledSessionFilters,
          );
          presetFilters();

          liveSessionsStore.ready = true;
        }
      },
    );
  }, [classesStore.premiumEnrollmentsReloaded, liveSessionsStore]);

  const loadingElement = (
    <div key="catalog-loading" className="catalog__loading-container">
      <CenteredLoadingSpinner />
    </div>
  );

  const renderContent = () => {
    const loading = !(
      liveSessionsStore.ready || classesStore.premiumEnrollmentsReloaded
    );
    if (loading) {
      return (
        <div className="live-sessions__loading-container">
          <CenteredLoadingSpinner />
        </div>
      );
    }

    if (!paginatedEntries.length) {
      return (
        <EmptyState>
          <EmptyStateIcon icon={CatalogIcon} />
          <Title size="lg" headingLevel="h4">
            {t('No Live Session found')}
          </Title>
          <EmptyStateBody>
            {t(
              'No results match the filter criteria. Change the filters and try again.',
            )}
          </EmptyStateBody>
        </EmptyState>
      );
    }

    if (newCardsEnabled) {
      return (
        <div className="live-sessions__container">
          <CatalogLayout.Grid>
            <Pagination
              className="live-sessions__pagination"
              itemCount={totalCount}
              perPage={entriesPerPage}
              page={currentPage}
              onSetPage={(e, n) => setCurrentPage(n)}
              onPerPageSelect={(e, n) => {
                liveSessionsStore.entriesPerPage = n;
              }}
              perPageOptions={pageOptions}
              widgetId="pagination-top"
              ouiaId="PaginationTop"
              aria-label={t('Top pagination controls')}
              variant={PaginationVariant.bottom}
            />
            <React.Suspense fallback={loadingElement}>
              <LiveSessionList paginatedEntries={paginatedEntries} />
            </React.Suspense>
            <Pagination
              className="live-sessions__pagination"
              itemCount={totalCount}
              perPage={entriesPerPage}
              page={currentPage}
              onSetPage={(e, n) => setCurrentPage(n)}
              onPerPageSelect={(e, n) => {
                liveSessionsStore.entriesPerPage = n;
              }}
              perPageOptions={pageOptions}
              widgetId="pagination-bottom"
              ouiaId="PaginationBottom"
              aria-label={t('Bottom pagination controls')}
              variant={PaginationVariant.bottom}
            />
          </CatalogLayout.Grid>
        </div>
      );
    }

    return (
      <>
        <LegacyLiveSessionList paginatedEntries={paginatedEntries} />
        <UltimatePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
        />
      </>
    );
  };

  return (
    <>
      <Helmet title={`${pageTitle} - Red Hat Learning`} />
      <CatalogLayout>
        <CatalogLayout.Filters>
          {/* TODO: filters loading state with skeletons */}
          <Filters showCatalogFilters />
        </CatalogLayout.Filters>
        <CatalogLayout.Content className="catalog__content">
          {userStore.hasReachedCurrentSubscriptionPremiumVTLimit &&
            userStore.isLimitedTrialSubscriber && (
              <LimitedTrialLimitReachAlert />
            )}
          {renderContent()}
        </CatalogLayout.Content>
      </CatalogLayout>
    </>
  );
};

export default withErrorBoundary(
  observer(LiveSessions),
  GenericViewError,
  handleError,
);
